import React from 'react';
import { Lightbulb, TrendingUp, AlertTriangle, Calendar, Target, Zap, ArrowRight } from 'lucide-react';

/**
 * InsightCard — Snapshot card for analytics insights.
 *
 * Props:
 *   insight: { title, summary, category, priority, metric_value, created_at,
 *               source_type, next_action, next_action_label }
 *
 * Trust labels:
 *   source_type: 'deterministic' | 'generated' | 'partial' | 'live'
 */

const SOURCE_CONFIG = {
    live:          { label: 'LIVE',            color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7' },
    deterministic: { label: 'DETERMINISTIC',   color: '#3b82f6', bg: '#eff6ff', border: '#93c5fd' },
    generated:     { label: 'AI-GENERATED',    color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd' },
    partial:       { label: 'PARTIAL DATA',    color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
    incomplete:    { label: 'INCOMPLETE',      color: '#9ca3af', bg: '#f9fafb', border: '#e5e7eb' },
};

const CATEGORY_ICONS = {
    growth:    TrendingUp,
    risk:      AlertTriangle,
    usage:     Target,
    friction:  Zap,
    default:   Lightbulb,
};

const CATEGORY_COLORS = {
    growth:   '#10b981',
    risk:     '#ef4444',
    usage:    '#6366f1',
    friction: '#f59e0b',
    default:  '#8b5cf6',
};

const PRIORITY_STYLES = {
    high:   { label: 'HIGH',   color: '#ef4444', bg: '#fef2f2' },
    medium: { label: 'MED',    color: '#f59e0b', bg: '#fffbeb' },
    low:    { label: 'LOW',    color: '#6b7280', bg: '#f3f4f6' },
};

const InsightCard = ({ insight, onAction }) => {
    const Icon = CATEGORY_ICONS[insight.category] || CATEGORY_ICONS.default;
    const color = CATEGORY_COLORS[insight.category] || CATEGORY_COLORS.default;
    const priorityStyle = PRIORITY_STYLES[insight.priority] || PRIORITY_STYLES.low;
    const srcConfig = SOURCE_CONFIG[insight.source_type] || SOURCE_CONFIG.generated;

    const hasAction = !!insight.next_action;

    return (
        <div style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 14,
            padding: '16px 18px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            borderLeft: `4px solid ${color}`,
            transition: 'box-shadow 0.2s',
        }}>
            {/* Header row: icon + title + priority badge */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: `${color}14`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, marginTop: 1,
                }}>
                    <Icon size={13} style={{ color }} />
                </div>
                <span style={{
                    fontSize: 13, fontWeight: 600, color: '#111827',
                    flex: 1, lineHeight: 1.35,
                }}>
                    {insight.title || 'Insight'}
                </span>
                {insight.priority && (
                    <span style={{
                        fontSize: 9, fontWeight: 700, padding: '2px 6px',
                        borderRadius: 4, letterSpacing: '0.05em',
                        background: priorityStyle.bg,
                        color: priorityStyle.color,
                        flexShrink: 0,
                    }}>
                        {priorityStyle.label}
                    </span>
                )}
            </div>

            {/* Trust source label — explicit text, not just color */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
                    padding: '2px 7px', borderRadius: 4,
                    background: srcConfig.bg,
                    color: srcConfig.color,
                    border: `1px solid ${srcConfig.border}`,
                }}>
                    {srcConfig.label}
                </span>
                {insight.metric_value !== undefined && insight.metric_value !== null && (
                    <span style={{ fontSize: 11, fontWeight: 600, color }}>
                        {insight.metric_value}
                    </span>
                )}
                {insight.created_at && (
                    <span style={{
                        fontSize: 10, color: '#94a3b8',
                        display: 'flex', alignItems: 'center', gap: 3,
                        marginLeft: 'auto',
                    }}>
                        <Calendar size={10} />
                        {new Date(insight.created_at).toLocaleDateString()}
                    </span>
                )}
            </div>

            {/* Summary */}
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0, lineHeight: 1.55 }}>
                {insight.summary || insight.description || ''}
            </p>

            {/* Next-step action affordance */}
            {hasAction && (
                <button
                    onClick={() => onAction && onAction(insight.next_action, insight)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '7px 12px', borderRadius: 8,
                        background: `${color}0d`, border: `1px solid ${color}30`,
                        color, fontSize: 11, fontWeight: 600,
                        cursor: 'pointer', textAlign: 'left',
                        marginTop: 2,
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = `${color}18`; }}
                    onMouseOut={e => { e.currentTarget.style.background = `${color}0d`; }}
                >
                    <ArrowRight size={11} />
                    {insight.next_action_label || 'Review →'}
                </button>
            )}
        </div>
    );
};


export default InsightCard;
